
import { defineComponent, inject, nextTick } from "vue";
import APList from "@/components/APList.vue";
import InspectionCard from "@/components/InspectionCard.vue";
import APForm, { APFormData } from "@/components/APForm.vue";
import DeleteAPForm, { DeleteAPFormData } from "@/components/DeleteAPForm.vue";
import Modal from "@/components/Modal.vue";
import { AP, DeviceCount, DeviceDetection } from "@/domain/model";
import { AdminAPIClientKey } from "@/symbols";
import AdminAPIClient from "@/domain/repository/admin_api_client";

type DataType = {
  aps: AP[];
  selectedAP?: AP;

  showAddAPForm: boolean;
  showEditAPForm: boolean;
  showDeleteAPForm: boolean;

  addAPFormData: APFormData;
  editAPFormData: APFormData;
  deleteAPFormData: DeleteAPFormData;

  aggrigationUnit: string;

  congestionLevel: number;
  deviceDetections: DeviceDetection[];
  deviceCountLog: DeviceCount[];
};

export default defineComponent({
  name: "Inspection",
  setup() {
    const adminAPIClient: AdminAPIClient | undefined = inject(
      AdminAPIClientKey
    );
    return {
      adminAPIClient,
    };
  },
  data(): DataType {
    return {
      // APs
      aps: [],
      selectedAP: undefined,

      // modal controls
      showAddAPForm: false,
      showEditAPForm: false,
      showDeleteAPForm: false,

      // Add AP form data
      addAPFormData: new APFormData(),

      // Edit AP form data
      editAPFormData: new APFormData(),

      // Delete AP form data
      deleteAPFormData: new DeleteAPFormData(),

      aggrigationUnit: "minute",

      congestionLevel: 0,
      deviceDetections: [],
      deviceCountLog: [],
    };
  },
  methods: {
    onSelectAP({ ap }: { ap: AP }) {
      this.selectedAP = ap;
      this.resetData();
    },

    onAdd() {
      this.addAPFormData = new APFormData();
      this.showAddAPForm = true;
    },

    onEdit({ ap }: { ap: AP }) {
      console.log(ap);
      this.editAPFormData = new APFormData({
        mac: ap.mac,
        name: ap.name,
        rssiThreshold: ap.rssiThreshold,
        stayThreshold: ap.stayThreshold,
        congestionBorder1: ap.congestionBorder1,
        congestionBorder2: ap.congestionBorder2,
      });
      this.showEditAPForm = true;
    },

    onDelete({ ap }: { ap: AP }) {
      this.deleteAPFormData = new DeleteAPFormData({
        mac: ap.mac,
        deleteConfirm: "",
      });
      this.showDeleteAPForm = true;
    },

    // AddAPForm
    onCancelAddAPForm() {
      this.showAddAPForm = false;
    },

    onChangeAddAPForm(d: APFormData) {
      if (!this.showAddAPForm) {
        return;
      }
      Object.assign(this.addAPFormData, d);
    },

    onOKAddAPForm() {
      const ap = new AP({
        mac: this.addAPFormData.mac,
        name: this.addAPFormData.name,
        rssiThreshold: this.addAPFormData.rssiThreshold,
        stayThreshold: this.addAPFormData.stayThreshold,
        congestionBorder1: this.addAPFormData.congestionBorder1,
        congestionBorder2: this.addAPFormData.congestionBorder2,
      });

      this.adminAPIClient
        ?.createAP(ap)
        .then((res) => {
          console.log("success");
        })
        .then(() => {
          // close dialog
          this.showAddAPForm = false;
        })
        .then(() => {
          this.refreshAPList();
        })
        .catch((err) => {
          console.error(err);
        });
    },

    // EditAPForm
    onCancelEditAPForm() {
      this.showEditAPForm = false;
    },

    onChangeEditAPForm(d: APFormData) {
      if (!this.showEditAPForm) {
        return;
      }
      Object.assign(this.editAPFormData, d);
    },

    onOKEditAPForm() {
      const ap = new AP({
        mac: this.editAPFormData.mac,
        name: this.editAPFormData.name,
        rssiThreshold: this.editAPFormData.rssiThreshold,
        stayThreshold: this.editAPFormData.stayThreshold,
        congestionBorder1: this.editAPFormData.congestionBorder1,
        congestionBorder2: this.editAPFormData.congestionBorder2,
      });

      this.adminAPIClient
        ?.updateAP(ap)
        .then((res) => {
          console.log("success");
        })
        .then(() => {
          // close dialog
          this.showEditAPForm = false;
        })
        .then(() => {
          this.refreshAPList();
        })
        .catch((err) => {
          console.error(err);
        });
    },

    // DeleteAPForm
    onCancelDeleteAPForm() {
      this.showDeleteAPForm = false;
    },

    onOKDeleteAPForm() {
      if (this.deleteAPFormData.mac === undefined) {
        console.warn("mac is undefined");
        return;
      }

      this.adminAPIClient
        ?.deleteAP(this.deleteAPFormData.mac)
        .then((res) => {
          console.log("success");
        })
        .then(() => {
          // close dialog
          this.showDeleteAPForm = false;
        })
        .then(() => {
          this.refreshAPList();
        })
        .catch((err) => {
          console.error(err);
        });
    },

    onDeleteAPFormChange(formData: any) {
      if (!this.showDeleteAPForm) {
        return;
      }
      Object.assign(this.deleteAPFormData, formData);
    },

    // chart events
    onChangeAggregationUnit({ ap, unit }: { ap: AP; unit: string }) {
      this.aggrigationUnit = unit;
      this.refreshDeviceDeviceCountLog();
    },

    // utility methods
    resetData() {
      this.congestionLevel = 0;
      this.deviceDetections = [];
      this.deviceCountLog = [];

      this.refreshDeviceCongestionLevel()
        .then(() => {
          return this.refreshDeviceDetectionList();
        })
        .then(() => {
          return this.refreshDeviceDeviceCountLog();
        });
    },

    async refreshAPList(): Promise<void> {
      return this.adminAPIClient
        ?.getAPList()
        .then((res) => {
          this.aps = res.aps;
          // APがない場合は選択を解除する。
          if (this.aps.length === 0) {
            this.selectedAP = undefined;
            return;
          }
          // APが選択されていない場合は0番目を選択する。
          if (this.selectedAP === undefined) {
            this.selectedAP = this.aps[0];
            return;
          }
          // APが存在しなくなった場合は選択を解除する。
          const n = this.aps.findIndex((ap) => ap.mac === this.selectedAP?.mac);
          if (n < 0) {
            this.selectedAP = undefined;
            return;
          }
          // もともと洗濯していたAPを選択しなおす。
          this.selectedAP = this.aps[n];
        })
        .catch((err) => {
          console.error(err);
        });
    },

    async refreshDeviceCongestionLevel(): Promise<void> {
      const mac = this.selectedAP?.mac;
      if (mac === undefined) {
        return;
      }

      this.adminAPIClient
        ?.getCongestionLevel(mac)
        .then((res) => {
          this.congestionLevel = res.congestionLevel.level ?? 0;
        })
        .catch((err) => {
          console.error(err);
        });
    },

    async refreshDeviceDetectionList(): Promise<void> {
      const mac = this.selectedAP?.mac;
      if (mac === undefined) {
        return;
      }

      this.adminAPIClient
        ?.getDeviceDetections(mac)
        .then((res) => {
          this.deviceDetections = res.deviceDetections;
        })
        .catch((err) => {
          console.error(err);
        });
    },

    async refreshDeviceDeviceCountLog(): Promise<void> {
      const mac = this.selectedAP?.mac;
      if (mac === undefined) {
        return;
      }

      this.adminAPIClient
        ?.getDeviceCountLog(mac, this.aggrigationUnit)
        .then((res) => {
          this.deviceCountLog = res.deviceCounts;
        })
        .catch((err) => {
          console.error(err);
        });
    },
  },
  computed: {
    sortedAPs(): AP[] {
      const aps = [...this.aps];
      return aps.sort((a: AP, b: AP) => {
        const an = a.name;
        const bn = b.name;
        if (an !== undefined && bn !== undefined) {
          const n = an.localeCompare(bn);
          if (n !== 0) {
            return n;
          }
        }

        const am = a.mac;
        const bm = b.mac;
        if (am !== undefined && bm !== undefined) {
          const n = am.localeCompare(bm);
          if (n !== 0) {
            return n;
          }
        }
        return 0;
      });
    },
    addAPFormOKButtonDisabled(): boolean {
      return (this.addAPFormData.mac ?? "") === "";
    },

    deleteAPFormOKButtonDisabled(): boolean {
      return this.deleteAPFormData.deleteConfirm !== "delete";
    },
  },
  mounted() {
    this.refreshAPList().then(() => {
      if (this.aps.length > 0) {
        this.selectedAP = this.aps[0];
        this.resetData();
      }
    });

    setInterval(this.refreshDeviceCongestionLevel, 5000);
    setInterval(this.refreshDeviceDetectionList, 5000);
    setInterval(this.refreshDeviceDeviceCountLog, 60 * 1000);
  },
  components: {
    APList,
    InspectionCard,
    APForm,
    DeleteAPForm,
    Modal,
  },
});
